import { Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import {
  BanknoteIcon,
  DownloadIcon,
  ShoppingCartIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { BarChart } from "@/shared/ui/BarChart";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { usePackageSalesByClassTypeQuery } from "../api/queries";
import {
  ClassTypeSalesReportFilter,
  type ClassTypeSalesReportSearch,
} from "../ui/ClassTypeSalesReportFilter";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
  reportSummaryCount,
} from "../ui/ReportSummaryStatCard";
import { ClassTypeSalesReportTable } from "../ui/ClassTypeSalesReportTable";

function useReportQuery(search: ClassTypeSalesReportSearch) {
  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const dateFrom = search.dateFrom ?? defaultFrom;
  const dateTo = search.dateTo ?? defaultTo;

  const query = useMemo(
    () => ({
      dateFrom,
      dateTo,
      levelId: search.levelId,
      sortBy: search.sortBy ?? "revenue",
    }),
    [dateFrom, dateTo, search.levelId, search.sortBy],
  );

  return { dateFrom, dateTo, query };
}

function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (
    s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function ClassTypeSalesReportPage() {
  const search = useSearch({
    from: "/app/reports/class-type-sales",
  }) as ClassTypeSalesReportSearch;

  const { dateFrom, dateTo, query } = useReportQuery(search);

  const { data: byClassType = [], isLoading } =
    usePackageSalesByClassTypeQuery(query);

  const totals = useMemo(
    () => ({
      salesCount: byClassType.reduce((sum, row) => sum + row.salesCount, 0),
      revenue: byClassType.reduce((sum, row) => sum + row.revenue, 0),
    }),
    [byClassType],
  );

  const exportCsv = useCallback(() => {
    const header = "ປະເພດຄລາດສ໌,ຈຳນວນທີ່ຂາຍ,ຍອດຂາຍ";
    const rows = byClassType.map(
      (r) =>
        `${escapeCsvCell(r.classTypeName)},${escapeCsvCell(r.salesCount)},${escapeCsvCell(r.revenue)}`,
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `class-type-sales-report-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [byClassType, dateFrom, dateTo]);

  const barChartData = useMemo(
    () =>
      byClassType.slice(0, 10).map((r) => ({
        classType:
          r.classTypeName.length > 20
            ? `${r.classTypeName.slice(0, 18)}...`
            : r.classTypeName,
        revenue: r.revenue,
      })),
    [byClassType],
  );

  const barChartConfig: ChartConfig = {
    revenue: { label: "ຍອດຂາຍ", color: "var(--chart-1)" },
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ລາຍງານຍອດຂາຍຕາມປະເພດຄລາດສ໌" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ລາຍງານຍອດຂາຍຕາມປະເພດຄລາດສ໌</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ສະແດງຈຳນວນທີ່ຂາຍແລະຍອດຂາຍແພັກເກດຄລາສ໌ແຍກຕາມປະເພດຄລາດສ໌ (ກັ່ນຕາມວັນທີຈ່າຍ)
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={byClassType.length === 0}
          >
            <DownloadIcon className="mr-2 size-4" />
            ດາວໂຫລດ CSV
          </Button>
        </div>

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <ClassTypeSalesReportFilter />
        </div>

        <ReportSummaryStatGrid columns={2}>
          <ReportSummaryStatCard
            title="ຈຳນວນທີ່ຂາຍທັງໝົດ"
            icon={ShoppingCartIcon}
            tone="attendance"
            isLoading={isLoading}
            value={reportSummaryCount(totals.salesCount)}
          />
          <ReportSummaryStatCard
            title="ຍອດຂາຍທັງໝົດ"
            icon={BanknoteIcon}
            tone="revenue"
            isLoading={isLoading}
            amountKip={totals.revenue}
          />
        </ReportSummaryStatGrid>

        {barChartData.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>ຍອດຂາຍຕາມປະເພດຄລາດສ໌ (Top 10)</CardTitle>
              <p className="text-muted-foreground text-sm">
                ຍອດຂາຍແພັກເກດຄລາສ໌ແຕ່ລະປະເພດ
              </p>
            </CardHeader>
            <CardContent>
              <BarChart
                data={barChartData}
                config={barChartConfig}
                xDataKey="classType"
                valueDataKey="revenue"
                xAxisTickFormatter={(v) =>
                  typeof v === "string" && v.length > 15
                    ? `${v.slice(0, 12)}...`
                    : String(v)
                }
                className="mx-auto aspect-video max-h-[320px]"
              />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>ຕາຕະລາງຍອດຂາຍແຍກຕາມປະເພດຄລາດສ໌</CardTitle>
            <p className="text-muted-foreground text-sm">
              ແພັກຫຼາຍປະເພດຄລາດສ໌ຈະແບ່ງຍອດຂາຍຕາມຈຳນວນປະເພດຄລາດສ໌ທີ່ໃຊ້ໄດ້
            </p>
          </CardHeader>
          <CardContent>
            <ClassTypeSalesReportTable
              isLoading={isLoading}
              data={byClassType}
            />
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
